import { __extends, __assign } from 'tslib';
import React__default, { createRef } from 'react';
import throttle from 'lodash.throttle';
import clsx from 'clsx';
import { propTypes, defaultProps } from './SidebarHeader.types.js';
import styles from './SidebarHeader.module.scss.js';

var SidebarHeader = /** @class */ (function (_super) {
    __extends(SidebarHeader, _super);
    function SidebarHeader(props) {
        var _this = _super.call(this, props) || this;
        _this.state = {
            shouldTruncateCenterElement: false,
            isTruncateTransitionInProgress: false,
            isTruncateTransitionOnClickInProgress: false,
        };
        _this.containerRef = createRef();
        _this.throttledHandleTruncateScroll = throttle(_this.handleTruncateScroll.bind(_this), 50);
        _this.handleTruncateClickOutside =
            _this.handleTruncateClickOutside.bind(_this);
        _this.setupTruncateEventListeners =
            _this.setupTruncateEventListeners.bind(_this);
        _this.removeTruncateEventListeners =
            _this.removeTruncateEventListeners.bind(_this);
        _this.handleTruncateTransitionEnd =
            _this.handleTruncateTransitionEnd.bind(_this);
        _this.adjustTruncatableMaxHeight =
            _this.adjustTruncatableMaxHeight.bind(_this);
        return _this;
    }
    SidebarHeader.prototype.componentDidMount = function () {
        if (this.props.dark) {
            console.warn('DEPRECATION WARNING: The `dark` prop is deprecated and support for it will be removed soon. Please use `variant` instead.');
        }
        if (this.props.truncateOnScroll && this.containerRef.current !== null) {
            this.setupTruncateEventListeners();
            this.adjustTruncatableMaxHeight();
        }
    };
    SidebarHeader.prototype.componentDidUpdate = function (prevProps) {
        if (prevProps.truncateOnScrollTarget !==
            this.props.truncateOnScrollTarget) {
            this.setupTruncateEventListeners(prevProps.truncateOnScrollTarget);
        }
        if (prevProps.centerElement !== this.props.centerElement) {
            this.adjustTruncatableMaxHeight();
        }
    };
    SidebarHeader.prototype.componentWillUnmount = function () {
        this.removeTruncateEventListeners();
    };
    SidebarHeader.prototype.setupTruncateEventListeners = function (previousTruncateOnScrollTarget) {
        var _a;
        if (this.props.truncateOnScrollTarget) {
            this.props.truncateOnScrollTarget.addEventListener('scroll', this.throttledHandleTruncateScroll);
            // Remove the event listener from window that could be attached after mount
            window.removeEventListener('scroll', this.throttledHandleTruncateScroll);
        }
        else {
            window.addEventListener('scroll', this.throttledHandleTruncateScroll);
        }
        if (previousTruncateOnScrollTarget) {
            // Remove the event listener from previous scroll target
            previousTruncateOnScrollTarget.removeEventListener('scroll', this.throttledHandleTruncateScroll);
        }
        // We want to use capture over bubble to avoid any "stale" events triggering the callback
        // By "stale" events we mean events that were dispatched during e.g. page change which (most likely involves DOM changes)
        window.addEventListener('click', this.handleTruncateClickOutside, true);
        (_a = this.containerRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('transitionend', this.handleTruncateTransitionEnd);
    };
    SidebarHeader.prototype.removeTruncateEventListeners = function () {
        var _a, _b;
        window.removeEventListener('scroll', this.throttledHandleTruncateScroll);
        (_a = this.props.truncateOnScrollTarget) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', this.throttledHandleTruncateScroll);
        window.removeEventListener('click', this.handleTruncateClickOutside, true);
        (_b = this.containerRef.current) === null || _b === void 0 ? void 0 : _b.removeEventListener('transitionend', this.handleTruncateTransitionEnd);
    };
    SidebarHeader.prototype.handleTruncateScroll = function () {
        var _a, _b;
        if (this.containerRef.current === null) {
            return;
        }
        // If truncation after click event is in progress we need to ignore scroll events to prevent incorrect truncation states
        if (this.state.isTruncateTransitionOnClickInProgress) {
            return;
        }
        var targetElementScrollHeight = this.props.truncateOnScrollTarget
            ? this.props.truncateOnScrollTarget.scrollHeight
            : document.documentElement.scrollHeight;
        var targetElementClientHeight = this.props.truncateOnScrollTarget
            ? this.props.truncateOnScrollTarget.clientHeight
            : document.documentElement.clientHeight;
        // Element has no scroll so we shouldn't change the truncation
        if (targetElementScrollHeight <= targetElementClientHeight) {
            return;
        }
        var targetElementScroll = this.props.truncateOnScrollTarget
            ? this.props.truncateOnScrollTarget.scrollTop
            : window.scrollY;
        var shouldTruncate = targetElementScroll > this.containerRef.current.scrollTop;
        if (this.state.shouldTruncateCenterElement !== shouldTruncate) {
            this.setState({
                shouldTruncateCenterElement: shouldTruncate,
                isTruncateTransitionInProgress: shouldTruncate,
            }, this.adjustTruncatableMaxHeight);
            (_b = (_a = this.props).onTruncateChange) === null || _b === void 0 ? void 0 : _b.call(_a, shouldTruncate);
        }
    };
    SidebarHeader.prototype.handleTruncateClickOutside = function (event) {
        var _a, _b, _c, _d;
        if (this.containerRef.current === null) {
            return;
        }
        // If already truncated and clicked outside the header do nothing
        if (this.state.shouldTruncateCenterElement &&
            !this.containerRef.current.contains(event.target)) {
            return;
        }
        // If clicked outside the header truncate
        if (!this.state.shouldTruncateCenterElement &&
            !this.containerRef.current.contains(event.target)) {
            this.setState({
                shouldTruncateCenterElement: true,
                isTruncateTransitionInProgress: true,
                isTruncateTransitionOnClickInProgress: true,
            }, this.adjustTruncatableMaxHeight);
            (_b = (_a = this.props).onTruncateChange) === null || _b === void 0 ? void 0 : _b.call(_a, true);
            return;
        }
        // If truncated and clicked inside the header, remove truncation
        if (this.state.shouldTruncateCenterElement) {
            this.setState({
                shouldTruncateCenterElement: false,
                isTruncateTransitionInProgress: true,
                isTruncateTransitionOnClickInProgress: true,
            }, this.adjustTruncatableMaxHeight);
            (_d = (_c = this.props).onTruncateChange) === null || _d === void 0 ? void 0 : _d.call(_c, false);
        }
    };
    SidebarHeader.prototype.handleTruncateTransitionEnd = function () {
        // Cancel any pending scroll events as they might interrupt truncation
        this.throttledHandleTruncateScroll.cancel();
        this.setState({
            isTruncateTransitionInProgress: false,
            isTruncateTransitionOnClickInProgress: false,
        });
    };
    SidebarHeader.prototype.adjustTruncatableMaxHeight = function () {
        var _this = this;
        requestAnimationFrame(function () {
            var _a, _b;
            if (!_this.containerRef.current) {
                return;
            }
            if (!_this.state.shouldTruncateCenterElement) {
                _this.containerRef.current.style.maxHeight = ((_b = (_a = _this.containerRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.scrollHeight) + "px";
            }
            else {
                _this.containerRef.current.style.maxHeight =
                    _this.props.truncatedMaxHeight || '44px';
            }
        });
    };
    SidebarHeader.prototype.render = function () {
        var _a, _b;
        var _c = this.props, className = _c.className, leftElement = _c.leftElement, centerElement = _c.centerElement, rightElement = _c.rightElement, alignTitle = _c.alignTitle, variant = _c.variant, dark = _c.dark, truncateOnScroll = _c.truncateOnScroll;
        var _d = this.state, shouldTruncate = _d.shouldTruncateCenterElement, isTruncateTransitionInProgress = _d.isTruncateTransitionInProgress;
        return (React__default.createElement("div", { ref: this.containerRef, "data-testid": "SidebarHeaderContainer", className: clsx(styles.SidebarHeader, className, (_a = {},
                _a[styles.SidebarHeader___dark] = variant === 'dark' || dark,
                _a[styles.SidebarHeader___aqua] = variant === 'aqua',
                _a[styles.SidebarHeader___withLeftElement] = leftElement,
                _a[styles.SidebarHeader___truncatable] = truncateOnScroll,
                _a)) },
            React__default.createElement("div", { className: styles.SidebarHeader_leftElement }, leftElement),
            React__default.createElement("div", { className: clsx(styles.SidebarHeader_centerElement, (_b = {},
                    _b[styles.SidebarHeader_centerElement___centered] = alignTitle === 'center' && !truncateOnScroll,
                    _b[styles.SidebarHeader_centerElement___centeredTruncate] = alignTitle === 'center' && truncateOnScroll,
                    _b[styles.SidebarHeader_centerElement___truncated] = shouldTruncate && !isTruncateTransitionInProgress,
                    _b[styles.SidebarHeader_centerElement___truncatable] = truncateOnScroll,
                    _b)) }, centerElement),
            React__default.createElement("div", { className: styles.SidebarHeader_rightElement }, rightElement)));
    };
    SidebarHeader.displayName = 'SidebarHeader';
    SidebarHeader.propTypes = propTypes;
    SidebarHeader.defaultProps = __assign({}, defaultProps);
    return SidebarHeader;
}(React__default.Component));

export { SidebarHeader as default };
